/**
 * RTL (Right-to-Left) Utilities
 * Hebrew is the primary RTL language for TendedLoop
 */
import { RTL_LANGUAGES, LANGUAGES } from './types';
/**
 * Check if a language is RTL
 * @param language - Language code (e.g., 'he', 'en')
 * @returns true if the language is RTL
 */
export function isRTL(language) {
    return RTL_LANGUAGES.includes(language);
}
/**
 * Get document direction for a language
 * @param language - Language code
 * @returns 'rtl' or 'ltr'
 */
export function getDirection(language) {
    return isRTL(language) ? 'rtl' : 'ltr';
}
/**
 * Get language info by code
 * @param code - Language code
 * @returns Language info or undefined
 */
export function getLanguageInfo(code) {
    return LANGUAGES.find((lang) => lang.code === code);
}
/**
 * Get the native name of a language
 * @param code - Language code
 * @returns Native language name (e.g., 'עברית' for Hebrew)
 */
export function getNativeLanguageName(code) {
    const lang = getLanguageInfo(code);
    return lang?.native || code;
}
/**
 * Apply RTL direction to document
 * Call this when language changes
 * @param language - Language code
 */
export function applyDocumentDirection(language) {
    const dir = getDirection(language);
    document.documentElement.dir = dir;
    document.documentElement.lang = language;
}
/**
 * CSS class helper for RTL-aware styling
 * Returns appropriate class based on current direction
 * @param ltrClass - Class for LTR direction
 * @param rtlClass - Class for RTL direction
 * @param language - Current language
 * @returns The appropriate class
 */
export function rtlClass(ltrClass, rtlClass, language) {
    return isRTL(language) ? rtlClass : ltrClass;
}
/**
 * Get text alignment class based on direction
 * @param language - Language code
 * @returns 'text-start' (works for both RTL and LTR with logical properties)
 */
export function getTextAlignClass(language) {
    // Using logical properties, 'text-start' works correctly for both directions
    return 'text-start';
}
/**
 * Check if current language needs icon mirroring
 * Directional icons (arrows, chevrons) should be mirrored in RTL
 * @param language - Language code
 * @returns true if icons should be mirrored
 */
export function shouldMirrorIcons(language) {
    return isRTL(language);
}
//# sourceMappingURL=rtl.js.map